import { useEffect, useState, type ReactNode } from "react";
import { ShieldOff } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";

import { VoltEmpty } from "../ui/VoltEmpty";
import { VoltApiError } from "../voltApi";
import { useWorkshopAccess } from "./useWorkshopAccess";
import { WorkshopError } from "./WorkshopError";
import { errorText, workshopApi } from "./workshopApi";
import type { Committee, WorkshopStatus } from "./types";

export type WorkshopMeta = {
  status: WorkshopStatus | null;
  committees: Committee[];
  setStatus: (
    update: (s: WorkshopStatus | null) => WorkshopStatus | null,
  ) => void;
};

/** Access check, status and committees for every Werkstatt page; a 403 from the API shows the notice too. */
export const WorkshopFrame = ({
  children,
}: {
  children: (meta: WorkshopMeta) => ReactNode;
}) => {
  const access = useWorkshopAccess();
  const [status, setStatus] = useState<WorkshopStatus | null>(null);
  const [committees, setCommittees] = useState<Committee[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [denied, setDenied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (access !== "granted") return;
    let live = true;
    workshopApi
      .status()
      .then((s) => live && setStatus(s))
      .catch((e) => {
        if (!live) return;
        if (e instanceof VoltApiError && e.status === 403) setDenied(e.message);
        else setError(errorText(e));
      })
      .finally(() => live && setLoaded(true));
    workshopApi
      .committees()
      .then((c) => live && setCommittees(c))
      .catch(() => live && setCommittees([]));
    return () => {
      live = false;
    };
  }, [access]);

  if (access === "denied" || denied !== null) {
    return (
      <VoltEmpty
        icon={ShieldOff}
        title="Kein Zugang zur Werkstatt"
        description={denied ?? "Deinem Konto fehlt die Werkstatt-Rolle."}
      />
    );
  }
  if (access === "loading" || !loaded) {
    return (
      <div className="flex flex-col gap-2" aria-busy>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {error && <WorkshopError>{error}</WorkshopError>}
      {status && !status.konfiguriert && (
        <WorkshopError>
          Die Werkstatt ist noch nicht freigeschaltet (kein API-Zugang
          hinterlegt). Melde dich bei den Admins deiner Fraktion.
        </WorkshopError>
      )}
      {children({ status, committees, setStatus })}
    </div>
  );
};
